import { AxiosError } from 'axios';

/**
 * Error response type from API
 */
export interface ApiErrorResponse {
    detail?: string;
    error?: string;
    message?: string;
    status?: number;
}

/**
 * Custom error handler for API calls
 */
export class ApiError extends Error {
    status: number;
    detail?: string;

    constructor(status: number, message: string, detail?: string) {
        super(message);
        this.status = status;
        this.detail = detail;
        this.name = 'ApiError';
    }
}

/**
 * Parse error response from API
 */
export const parseApiError = (error: AxiosError): ApiError => {
    const status = error.response?.status || 500;
    const data = error.response?.data as ApiErrorResponse;

    const message = data?.detail || data?.error || data?.message || error.message;
    const detail = data?.detail;

    return new ApiError(status, message, detail);
};

/**
 * Check if error is authentication related
 */
export const isAuthError = (error: AxiosError): boolean => {
    return [401].includes(error.response?.status || 0);
};

/**
 * Handle authentication errors and redirect to login
 */
export const handleAuthError = (error: AxiosError, errorCallback?: (error: ApiError) => void): void => {
    if (!isAuthError(error)) {
        return;
    }
    // eslint-disable-next-line no-console
    console.warn(`[API Error Handler] Authentication error ${error.response?.status}`);

    // Call custom error callback if provided
    if (errorCallback) {
        errorCallback(parseApiError(error));
    }

    // Redirect to login if not already there
    const loginUrl = '/login';
    if (window.location.pathname !== loginUrl) {
        window.location.href = loginUrl;
    }
};

/**
 * Get user-friendly error message
 */
export const getUserFriendlyMessage = (status: number): string => {
    const messages: { [key: number]: string } = {
        400: 'Invalid request. Please check your input and try again.',
        401: 'Session expired. Please log in again.',
        403: 'You don\'t have permission to perform this action.',
        404: 'Resource not found.',
        409: 'Conflict. The resource already exists.',
        422: 'Validation error. Please check your input.',
        429: 'Too many requests. Please try again later.',
        500: 'Server error. Please try again later.',
        502: 'Bad gateway. Please try again later.',
        503: 'Service unavailable. Please try again later.',
    };

    return messages[status] || 'An error occurred. Please try again.';
};

/**
 * Log error for debugging (can be sent to error tracking service)
 */
export const logError = (error: AxiosError, context?: string): void => {
    const apiError = parseApiError(error);

    // eslint-disable-next-line no-console
    console.error(`[API Error] ${context || 'Unknown context'}`, {
        status: apiError.status,
        message: apiError.message,
        detail: apiError.detail,
        url: error.config?.url,
        method: error.config?.method,
        timestamp: new Date().toISOString(),
    });
};
